import styled from "@emotion/styled";
import { East } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Theme,
  useTheme,
} from "@mui/material";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { motion } from "framer-motion";
import React, { useState } from "react";
import HeroSection from "../Components/About/HeroSection";
import Footer from "../Components/Footer";
import { PageShell } from "../Components/PageShell";
import { submitHubspotContactForm } from "../Utils/hubspot";
import { scrollToAnchor } from "../Utils/scroll";
import { validateEmail } from "../Utils/strings";

const Contact: React.FC = () => {
  const theme = useTheme();
  const offices = [
    {
      title: "All Over",
      cityStateZip: "Planet Earth",
      address: "Remote",
      phone: "555-555-5555",
    },
    {
      title: "Denver",
      cityStateZip: "Denver, CO 80211",
      address: "123 Main Street",
      phone: "555-555-5555",
    },
    {
      title: "San Diego",
      cityStateZip: "Denver, CO 80211",
      address: "123 Main Street",
      phone: "555-555-5555",
    },
    {
      title: "Lake Tahoe",
      cityStateZip: "Denver, CO 80211",
      address: "123 Main Street",
      phone: "555-555-5555",
    },
  ];

  return (
    <PageShell>
      <Container maxWidth="xl">
        <Box component="div" paddingTop={12} marginBottom={8}>
          <TitleWrapper
            theme={theme}
            direction="row"
            alignItems="start"
            justifyContent="start"
          >
            <Title color="primary.500">Let's Get</Title>
            <Box
              component="div"
              className="funky-text"
              paddingLeft={3}
              paddingRight={10}
            >
              Weird
            </Box>
          </TitleWrapper>

          <Stack gap={6}>
            <Stack gap={3} direction={"column"}>
              <Typography variant="h2" color="primary.500">
                hello@penciltopolish.com
              </Typography>

              <HubspotForm />

              <Box component="div" mx={-2.5} mt={-3} mb={-28}>
                <HeroSection
                  title="Locations"
                  description="We are a remote first company and can work with anyone in any timezone around the world. You can find us working in the following locations and we would love to collaborate in person."
                />
              </Box>
            </Stack>

            <Grid2 container>
              {offices.map((office, index) => {
                return (
                  <Grid2 xs={12} sm={12} md={6} lg={3} key={index}>
                    <Stack
                      width="fit-content"
                      gap={2}
                      paddingRight={3}
                      paddingLeft={index === 0 ? 0 : 3}
                      borderRight={index === 0 ? "1px solid #000" : "none"}
                    >
                      <Typography variant="h4">{office.title}</Typography>
                      <Stack>
                        <Typography variant="body1">
                          {office.cityStateZip}
                        </Typography>
                        <Typography variant="body1">
                          {office.address}
                        </Typography>
                        <Typography variant="body1">{office.phone}</Typography>
                      </Stack>
                    </Stack>
                  </Grid2>
                );
              })}
            </Grid2>
          </Stack>
        </Box>
      </Container>
      <Footer />
    </PageShell>
  );
};

export default Contact;

const Title = styled(Typography)`
  text-transform: uppercase;
  font-weight: 700;
  font-size: 6rem;
  line-height: 1;
`;

interface TitleWrapperProps {
  theme: any;
}

const TitleWrapper = styled(Stack)<TitleWrapperProps>`
  position: relative;
  margin-top: 64px;

  span {
    display: inline;
  }

  .funky-text {
    background: ${(props) => props.theme.palette.gradients.greenBlue};
    -webkit-text-fill-color: transparent;
    -webkit-background-clip: text;
    font-family: "woonderregular";
    background-size: 100% 75%;
    margin-bottom: -8rem;
    text-transform: none;
    pointer-events: none;
    position: relative;
    font-size: 8rem;
    display: inline;
    left: -5.5rem;
    top: -4.2rem;
  }
`;

declare global {
  interface Window {
    hbspt?: any;
  }
}

export const HubspotForm = (): JSX.Element => {
  const [formSuccessResponse, setFormSuccessResponse] = useState<string | null>(
    null
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);

  const [firstname, setFirstname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [company, setCompany] = useState<string>("");
  const [message, setMessage] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const [projectTypes, setProjectTypes] = useState<string[]>([]);
  const [timeline, setTimeline] = useState<string>("");
  const [budget, setBudget] = useState<string>("");

  const theme = useTheme();

  const submitContactForm = async () => {
    setIsValidating(true);

    if (
      firstname.length > 0 &&
      lastname.length > 0 &&
      validateEmail(email) &&
      company.length > 0 &&
      message.length > 0 &&
      timeline.length > 0 &&
      budget.length > 0
    ) {
      await submitHubspotContactForm(
        email,
        firstname,
        lastname,
        company,
        message,
        projectTypes.join(", "),
        timeline,
        budget
      ).then((data) => {
        setFormSuccessResponse(
          data.data.inlineMessage
            .replace(/<[^>]*>/g, "")
            .replace("\n", "<br/>") ?? "Thanks for connecting with us!"
        );

        setTimeout(() => {
          scrollToAnchor({ title: "Contact", link: "/#/contact" });
        }, 500);
      });
    }
  };

  return (
    <Stack
      bgcolor={"rgba(255,255,255,0.075)"}
      direction={"column"}
      alignItems={"end"}
      borderRadius={4}
      padding={3}
      gap={3}
    >
      {formSuccessResponse === null ? (
        <>
          <Stack
            justifyContent={"start"}
            alignItems={"start"}
            direction={"row"}
            width={"100%"}
            mb={-2}
          >
            <Typography variant="textXLSemiBold" color="white">
              Tell us about your project
            </Typography>
          </Stack>

          <Grid2 container spacing={2} pt={0}>
            <Grid2 xs={12} sm={12} md={12} lg={6} xl={6}>
              <FormTextField
                onChange={(value) => {
                  setFirstname(value.target.value);
                  setIsValidating(false);
                }}
                error={isValidating && firstname.length === 0}
                placeholder="First name *"
                aria-label="First name"
                variant="outlined"
                value={firstname}
                theme={theme}
                type="text"
                fullWidth
                required
              />
            </Grid2>
            <Grid2 xs={12} sm={12} md={12} lg={6} xl={6}>
              <FormTextField
                onChange={(value) => {
                  setLastname(value.target.value);
                  setIsValidating(false);
                }}
                error={isValidating && lastname.length === 0}
                placeholder="Last name *"
                aria-label="Last name"
                variant="outlined"
                value={lastname}
                theme={theme}
                type="text"
                fullWidth
                required
              />
            </Grid2>

            <Grid2 xs={12} sm={12} md={12} lg={6} xl={6}>
              <FormTextField
                onChange={(value) => {
                  setEmail(value.target.value);
                  setIsValidating(false);
                }}
                error={isValidating && !validateEmail(email)}
                placeholder="Email *"
                aria-label="Email"
                variant="outlined"
                value={email}
                theme={theme}
                type="email"
                inputProps={{
                  required: true,
                  pattern: "[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,}$",
                }}
                fullWidth
                required
              />
            </Grid2>

            <Grid2 xs={12} sm={12} md={12} lg={6} xl={6}>
              <FormTextField
                onChange={(value) => {
                  setCompany(value.target.value);
                  setIsValidating(false);
                }}
                error={isValidating && company.length === 0}
                placeholder="Company *"
                aria-label="Company"
                variant="outlined"
                value={company}
                theme={theme}
                type="text"
                fullWidth
                required
              />
            </Grid2>

            <Grid2 xs={12} sm={12} md={12} lg={12} xl={12}>
              <FormTextField
                onChange={(value) => {
                  setMessage(value.target.value);
                  setIsValidating(false);
                }}
                error={isValidating && message.length === 0}
                placeholder="Project details *"
                aria-label="Project details"
                variant="outlined"
                value={message}
                theme={theme}
                type="text"
                multiline
                fullWidth
                rows={4}
                required
              />
            </Grid2>

            <Grid2 xs={12} sm={12} md={12} lg={12} xl={12} mt={0.5}>
              <Typography variant="textXLSemiBold" color="white">
                What type of project?
              </Typography>
            </Grid2>

            <Grid2 xs={12} sm={12} md={12} lg={12} xl={12} mt={-2}>
              <Stack
                direction={{ xs: "column", sm: "column", md: "row" }}
                gap={{ xs: 0, sm: 0, md: 2 }}
                width="100%"
              >
                <Stack
                  direction="column"
                  alignItems="start"
                  sx={{ width: { xs: "100%", sm: "100%", md: "50%" } }}
                >
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "GenAI"]
                              : prev.filter((type) => type !== "GenAI")
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="GenAI"
                    sx={{ color: "white" }}
                  />
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "Website Development"]
                              : prev.filter(
                                  (type) => type !== "Website Development"
                                )
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="Website Development"
                    sx={{ color: "white" }}
                  />
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "Design"]
                              : prev.filter((type) => type !== "Design")
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="Design"
                    sx={{ color: "white" }}
                  />
                </Stack>
                <Stack
                  direction="column"
                  alignItems="start"
                  sx={{ width: { xs: "100%", sm: "100%", md: "50%" } }}
                >
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "Technology Consulting"]
                              : prev.filter(
                                  (type) => type !== "Technology Consulting"
                                )
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="Technology Consulting"
                    sx={{ color: "white" }}
                  />
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "Software Development"]
                              : prev.filter(
                                  (type) => type !== "Software Development"
                                )
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="Software Development"
                    sx={{ color: "white" }}
                  />
                  <ContactCheckbox
                    control={
                      <Checkbox
                        sx={{ color: "white" }}
                        onChange={(event) => {
                          const checked = event.target.checked;
                          setProjectTypes((prev) =>
                            checked
                              ? [...prev, "Other"]
                              : prev.filter((type) => type !== "Other")
                          );
                        }}
                      />
                    }
                    theme={theme}
                    label="Other"
                    sx={{ color: "white" }}
                  />
                </Stack>
              </Stack>
            </Grid2>

            <Grid2 xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                sx={{ mb: -0.8 }}
              >
                <Stack direction="column" width="100%">
                  <ContactSelect fullWidth theme={theme} size="small">
                    {timeline.length === 0 && (
                      <InputLabel sx={{ color: "white" }} shrink={false}>
                        Timeline *
                      </InputLabel>
                    )}
                    <Select
                      labelId="timeline-label"
                      id="timeline-select"
                      value={timeline}
                      onChange={(e) => {
                        setTimeline(e.target.value);
                        setIsValidating(false);
                      }}
                      required
                      sx={{
                        color: "white",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "white",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.yellow.main,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.yellow.main,
                        },
                        ".MuiSvgIcon-root": {
                          color: "white",
                        },
                      }}
                    >
                      <MenuItem value="1-3 months">1-3 months</MenuItem>
                      <MenuItem value="3-6 months">3-6 months</MenuItem>
                      <MenuItem value="6+ months">6+ months</MenuItem>
                    </Select>
                  </ContactSelect>

                  <SelectUnderline
                    theme={theme}
                    error={isValidating && timeline.length === 0}
                  />
                </Stack>

                <Stack direction="column" width="100%">
                  <ContactSelect fullWidth theme={theme} size="small">
                    {budget.length === 0 && (
                      <InputLabel sx={{ color: "white" }} shrink={false}>
                        Budget *
                      </InputLabel>
                    )}
                    <Select
                      labelId="budget-label"
                      id="budget-select"
                      value={budget}
                      onChange={(e) => {
                        setBudget(e.target.value);
                        setIsValidating(false);
                      }}
                      required
                      sx={{
                        color: "white",
                        ".MuiOutlinedInput-notchedOutline": {
                          borderColor: "white",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.yellow.main,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: theme.palette.yellow.main,
                        },
                        ".MuiSvgIcon-root": {
                          color: "white",
                        },
                      }}
                    >
                      <MenuItem value="< $50k">Less than $50k</MenuItem>
                      <MenuItem value="$50k-$100k">$50k-$100k</MenuItem>
                      <MenuItem value="$100k-$250k">$100k-$250k</MenuItem>
                      <MenuItem value="$250k+">$250k+</MenuItem>
                    </Select>
                  </ContactSelect>
                  <SelectUnderline
                    theme={theme}
                    error={isValidating && budget.length === 0}
                  />
                </Stack>
              </Stack>
            </Grid2>
          </Grid2>

          <motion.div
            style={{ transformOrigin: "right", width: "fit-content" }}
            whileHover={{ transform: "scale(1.02)" }}
            initial={{ transform: "scale(1)" }}
          >
            <Button
              onClick={() => submitContactForm()}
              variant="contained"
              title="Send"
              endIcon={
                <East
                  transform={"scale(1.5)"}
                  style={{ paddingLeft: "0.5rem" }}
                />
              }
              sx={{
                background: theme.palette.pink.main,
                color: theme.palette.common.white,
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 2,
                width: 160,
                mt: 1,
                "&:hover": {
                  background: `${theme.palette.pink.main}CC`,
                },
              }}
              disableElevation
            >
              <Typography variant="h6" fontWeight={700}>
                Send
              </Typography>
            </Button>
          </motion.div>
        </>
      ) : (
        <Typography variant="h6" color="white" width={"96%"} pb={2} pt={2}>
          <span
            dangerouslySetInnerHTML={{
              __html: formSuccessResponse,
            }}
          ></span>
        </Typography>
      )}
    </Stack>
  );
};

const FormTextField = styled(TextField)<{ theme: Theme; error: boolean }>`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 100px white inset !important;
    height: 0rem;
  }

  div[data-lastpass-icon-root] {
    display: none;
  }
  div[data-lastpass-infield] {
    display: none;
  }

  input::placeholder {
    color: ${(props) => props.theme.palette.common.white};
    opacity: 1;
  }

  &:after {
    background: ${(props) =>
      props.error
        ? props.theme.palette.pink.main
        : props.theme.palette.yellow.main};
    transform: translateX(-50%);
    border-radius: 5px;
    position: absolute;
    bottom: 0.05rem;
    height: 0.3rem;
    content: "";
    width: 98.9%;
    z-index: 0;
    left: 50%;
  }

  .MuiOutlinedInput-notchedOutline {
    border-width: 0px;
    outline: none;
    border: none;
  }

  .MuiOutlinedInput-root {
    background: ${(props) => props.theme.palette.primary.dark};
    color: ${(props) => props.theme.palette.common.white};
    border: solid 2px transparent;
    background-clip: padding-box;
    box-sizing: border-box;
    border-radius: 4px;
    position: relative;
    height: 3rem;
    z-index: 10;

    &.MuiInputBase-multiline {
      border-radius: 8px;
      height: inherit;

      textarea::placeholder {
        color: ${(props) => props.theme.palette.common.white};
        opacity: 1;
      }
    }

    &:hover {
      &:before {
        background: ${({ theme }) => theme.palette.gradients.yellowPink};
      }
    }

    &.Mui-focused {
      border: solid 0.99px transparent;

      &.MuiInputBase-multiline {
        margin-bottom: 3px;
      }

      &:before {
        background: ${({ theme }) => theme.palette.gradients.yellowPink};
      }
    }
  }

  .MuiInputLabel-root {
    color: ${(props) => props.theme.palette.common.white};

    &.Mui-focused {
      color: ${(props) => props.theme.palette.common.white};
    }
  }
`;

const ContactCheckbox = styled(FormControlLabel)<{ theme: Theme }>`
  .MuiCheckbox-root {
    svg {
      color: ${(props) => props.theme.palette.yellow.main};
    }
  }
`;

const ContactSelect = styled(FormControl)<{ theme: Theme }>`
  fieldset {
    background: ${(props) => props.theme.palette.primary.dark};
    color: ${(props) => props.theme.palette.common.white};
    border: solid 2px transparent;
    border-radius: 4px;
  }

  svg {
    z-index: 10;
  }

  .MuiInputBase-input {
    color: ${(props) => props.theme.palette.common.white};
    position: relative;
    z-index: 2;

    &::placeholder {
      color: ${(props) => props.theme.palette.common.white};
      opacity: 1;
    }
  }

  label.Mui-focused {
    color: ${(props) => props.theme.palette.common.white};
  }

  legend {
    color: ${(props) => props.theme.palette.common.white};
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: transparent !important;
  }
`;

const SelectUnderline = styled.div<{ theme: Theme; error: boolean }>`
  background: ${(props) =>
    props.error
      ? props.theme.palette.pink.main
      : props.theme.palette.yellow.main};
  margin-top: -0.24rem;
  border-radius: 5px;
  bottom: 0.05rem;
  height: 0.5rem;
  height: 0.3rem;
  width: 100%;
`;
